import React, { useState } from 'react';
import {
  Box,
  Typography,
  styled,
  Card,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Chip,
  Modal,
  IconButton,
  Snackbar,
} from '@mui/material';
import {
  Send as SendIcon,
  Info as InfoIcon,
  AttachFile as AttachFileIcon,
  PhotoSizeSelectActual as SizeIcon,
  Image as ImageIcon,
  PictureAsPdf as PdfIcon,
  ContentPaste as PasteIcon,
  ContentCopy as ContentCopyIcon,
  Check as CheckIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import FeedbackApiService from 'app/NewAIAvatarLayout/services/apiService/feedbackApiService';
import { selectDealerSelected } from '../../redux/headerSlice/selectors';

const ContentWrapper = styled(Box)(({ theme }) => ({
  padding: theme.spacing(4),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(4),
  minHeight: 'calc(100vh - 64px)', // Adjust based on header height if needed
}));

const PageTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.875rem',
  fontWeight: 700,
  color: theme.palette.text.primary,
}));

const FormWrapper = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  flex: 1,
});

const FormCard = styled(Card)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  boxShadow: 'none',
  width: '100%',
  maxWidth: 600,
  padding: theme.spacing(3),
}));

const DescriptionText = styled(Typography)(({ theme }) => ({
  fontSize: '0.9375rem',
  color: theme.palette.text.secondary,
  textAlign: 'center',
  marginBottom: theme.spacing(3),
  lineHeight: 1.6,
}));

const InstructionsContainer = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2),
  padding: theme.spacing(2),
  backgroundColor: theme.palette.background.default,
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.divider}`,
}));

const InstructionsHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  marginBottom: theme.spacing(1.5),
}));

const InstructionsTitle = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  fontWeight: 600,
  color: theme.palette.text.primary,
}));

const InstructionsList = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
}));

const InstructionItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: theme.spacing(1),
  fontSize: '0.8125rem',
  color: theme.palette.text.secondary,
}));

const InstructionIcon = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginTop: '2px',
  color: theme.palette.primary.main,
  flexShrink: 0,
}));

const InstructionText = styled(Typography)(({ theme }) => ({
  fontSize: '0.8125rem',
  color: theme.palette.text.secondary,
  lineHeight: 1.6,
  flex: 1,
}));

const FormatChipsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(0.5),
  marginTop: theme.spacing(0.5),
}));

const FormatChip = styled(Chip)(({ theme }) => ({
  height: '24px',
  fontSize: '0.75rem',
  fontWeight: 500,
}));

const FormContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(3),
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: theme.palette.background.default,
  },
}));

const StyledButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  padding: theme.spacing(1.5),
  fontWeight: 600,
}));

const SuccessModal = styled(Modal)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const ModalContent = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(3),
  maxWidth: 500,
  width: '90%',
  outline: 'none',
  boxShadow: theme.shadows[24],
}));

const ModalHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: theme.spacing(2),
}));

const FeedbackIdContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(1.5),
  backgroundColor: theme.palette.background.default,
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.divider}`,
  marginTop: theme.spacing(2),
}));

const FeedbackIdText = styled(Typography)(({ theme }) => ({
  fontFamily: 'monospace',
  fontSize: '0.875rem',
  color: theme.palette.text.primary,
  flex: 1,
  wordBreak: 'break-all',
}));

const CopyButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.primary.main,
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

export const FeedbackPage: React.FC = () => {
  const selectedDealer = useSelector(selectDealerSelected);
  
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [feedbackId, setFeedbackId] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // Get dealer ID from Redux if available
  const getDealerId = (): string | undefined => {
    if (selectedDealer && typeof selectedDealer === 'object') {
      return selectedDealer._id || selectedDealer.dealer;
    }
    return undefined;
  };

  // Get browser timezone
  const getBrowserTimezone = (): string => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch (error) {
      console.error('Error getting timezone:', error);
      return 'UTC';
    }
  };

  const handleCopyFeedbackId = async () => {
    if (feedbackId) {
      try {
        await navigator.clipboard.writeText(feedbackId);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (error) {
        console.error('Failed to copy feedback ID:', error);
        setErrorMessage('Failed to copy feedback ID');
      }
    }
  };

  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false);
    setFeedbackId(null);
    setCopied(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Reset messages
    setErrorMessage(null);

    // Validation
    if (!message.trim()) {
      setErrorMessage('Message is required');
      return;
    }

    setLoading(true);

    try {
      const timeZone = getBrowserTimezone();
      const dealerId = getDealerId();
      
      const response = await FeedbackApiService.submitFeedback({
        message: message.trim(),
        dealer_id: dealerId || undefined,
        time_zone: timeZone,
        images: [],
      });

      if (response.statusCode === 201 && response.statusText === 'success') {
        // Get feedback ID from response
        const id = response.body?.body?._id || response.body?._id || null;
        setFeedbackId(id);
        setShowSuccessModal(true);
        // Clear form
        setMessage('');
      } else {
        setErrorMessage(response.message || 'Failed to submit feedback');
      }
    } catch (error: any) {
      console.error('Error submitting feedback:', error);
      const errorMsg = error?.response?.data?.message || 
                      error?.message || 
                      'An error occurred while submitting feedback. Please try again.';
      setErrorMessage(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <ContentWrapper>
      <PageTitle>Feedback</PageTitle>
      
      <FormWrapper>
        <FormCard>
          <DescriptionText>
            This is a feedback form for MyAutoIQ services. Please share your thoughts, suggestions, or report any issues you've encountered. Your feedback helps us improve our services and provide you with a better experience.
          </DescriptionText>

          {errorMessage && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMessage(null)}>
              {errorMessage}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <FormContainer>
              <StyledTextField
                label="Message"
                variant="outlined"
                fullWidth
                required
                multiline
                rows={6}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                disabled={loading}
                placeholder="e.g., I would like to suggest adding a new feature that allows users to customize email templates with more formatting options..."
              />

              <StyledButton
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                disabled={loading || !message.trim()}
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
              >
                {loading ? 'Submitting...' : 'Submit Feedback'}
              </StyledButton>
            </FormContainer>
          </form>
        </FormCard>
      </FormWrapper>

      {/* Success Confirmation Modal */}
      <SuccessModal
        open={showSuccessModal}
        onClose={handleCloseSuccessModal}
        aria-labelledby="success-modal-title"
        aria-describedby="success-modal-description"
      >
        <ModalContent>
          <ModalHeader>
            <Typography id="success-modal-title" variant="h6" component="h2" sx={{ fontWeight: 600 }}>
              Thank You for Your Feedback!
            </Typography>
            <IconButton
              onClick={handleCloseSuccessModal}
              size="small"
              sx={{ color: 'text.secondary' }}
            >
              <CloseIcon />
            </IconButton>
          </ModalHeader>
          
          <Typography id="success-modal-description" sx={{ mb: 2, color: 'text.secondary' }}>
            Your feedback has been submitted successfully. We appreciate you taking the time to share your thoughts with us. Your input helps us improve our services and provide you with a better experience.
          </Typography>

          {feedbackId && (
            <Box>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: 'text.secondary' }}>
                Feedback ID:
              </Typography>
              <FeedbackIdContainer>
                <FeedbackIdText>{feedbackId}</FeedbackIdText>
                <CopyButton
                  onClick={handleCopyFeedbackId}
                  size="small"
                  title={copied ? 'Copied!' : 'Copy Feedback ID'}
                >
                  {copied ? (
                    <CheckIcon sx={{ fontSize: 18, color: 'success.main' }} />
                  ) : (
                    <ContentCopyIcon sx={{ fontSize: 18 }} />
                  )}
                </CopyButton>
              </FeedbackIdContainer>
            </Box>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleCloseSuccessModal}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
              }}
            >
              Close
            </Button>
          </Box>
        </ModalContent>
      </SuccessModal>

      {/* Copy Success Snackbar */}
      <Snackbar
        open={copied}
        autoHideDuration={2000}
        onClose={() => setCopied(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setCopied(false)} severity="success" sx={{ width: '100%' }}>
          Feedback ID copied to clipboard!
        </Alert>
      </Snackbar>
    </ContentWrapper>
  );
};

